<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import RecommendationsProductCard from "@retailer/svelte/Cart/Page/DistributorSection/RecommendationsProductCard.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import CartRecommendationsStore from "@retailer/svelte/stores/CartRecommendationsStore";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  import { isRestrictedRole } from "@retailer/services/preview_forbidden_flash";
  import type { Product } from "@shared/types/ProductLine";
  import type { CartOrderType } from "@shared/types/Cart";
  import { onDestroy } from "svelte";

  export let order: CartOrderType;
  export let recommendations: Product[];

  const distributor = recommendations[0]?.distributor;

  let cartProductIds = [];
  const unsubscribe = CartStore.subscribe((n: any) => {
    cartProductIds = (n?.orders || [])
      .map((order: any) => {
        return order.products.map((product: any) => product.product_id);
      })
      .flat();
  });

  onDestroy(() => {
    unsubscribe();
  });

  const products = recommendations.filter((product: any) => !cartProductIds.includes(product.id));
  const productsToShow = products.slice(0, 6);

  const stateSettings = $StateSettingsStore || {};
  let usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;

  let futurePrices;
  let futurePricesLoading = true;
  const futurePricesRes = async () => {
    const stagedRolloutDisabled = !stagedRolloutCheck("future_pricing");

    if (
      stagedRolloutDisabled ||
      isRestrictedRole(["supplier"]) ||
      !distributor?.sevenfifty_integrated ||
      !usesFuturePricingWithMinBottle
    ) {
      futurePricesLoading = false;
      CartRecommendationsStore.update(n => {
        n[distributor.id]["futurePrices"] = [];
        return n;
      });
      return;
    }

    const result = $CartRecommendationsStore[distributor.id]["futurePrices"];
    if (result) {
      futurePrices = result;
      futurePricesLoading = false;
      return;
    }

    await HttpPromise({
      method: "GET",
      url: "/api/retailer/future_prices",
      data: {
        inventory_ids: products.map(product => product.inventory_id),
        distributor_id: distributor.id,
      },
    })
      .then(res => {
        futurePrices = res.future_prices;
        futurePricesLoading = false;
        CartRecommendationsStore.update(n => {
          n[distributor.id]["futurePrices"] = res.future_prices;
          return n;
        });
      })
      .catch(() => {
        futurePricesLoading = false;
        CartRecommendationsStore.update(n => {
          n[distributor.id]["futurePrices"] = [];
          return n;
        });
      });
  };

  futurePricesRes();
</script>

<div data-testid="cart-recommendations-product-container">
  <div class="fizz-inset-square-16">
    <div class="fizz-surface-gray fizz-inset-square-12 fizz-flex-row-4">
      <div class="fizz-align-self-start" style="padding-top: .05rem;">
        <Icon fill="#595959" width={16} height={16} type="History" />
      </div>
      <p class="fizz-text-strong">Add items to your cart from past orders with this distributor.</p>
    </div>
  </div>

  {#if productsToShow.length}
    {#each productsToShow as product}
      <RecommendationsProductCard
        {product}
        products={productsToShow}
        {order}
        bind:futurePrices
        bind:usesFuturePricingWithMinBottle
        bind:futurePricesLoading
      />
    {/each}
  {:else}
    <div class="fizz-stack-16 fizz-text-center" data-testid="no-recommendations-left">
      All recommended products have been added to your cart.
    </div>
  {/if}
</div>
